import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { CopyDocument } from '@element-plus/icons-vue'
import { TripFull } from '@/models/trip'
import { normalizeUserName } from '@/helpers/user'
import { normalizeCarName } from '@/helpers/car-profile'
import { ElMessage } from 'element-plus'
import { formatDateWithTime } from '@/helpers/date'
import { centsToDollars, commonCase, normalizeFalsy } from '@/helpers/strings'
import { getTripKeyMeta } from '@/helpers/trip'
import EditableSelect from '@/components/EditableSelect.vue'
import { PrimitiveNonNull } from '@/models/common'
import { TripStatus } from '@/models/trip'
import { useTripsStore } from '@/store/trips'
import axios, { AxiosError } from 'axios'


export default /*@__PURE__*/_defineComponent({
  __name: 'DetailsSection',
  props: {
    trip: {}
  },
  setup(__props: any) {

const props = __props
const router = useRouter()

const userKeys: (keyof TripFull)[] = ['host', 'renter']
const dateKeys: (keyof TripFull)[] = ['start_date', 'end_date']
const navKeys: (keyof TripFull)[] = ['host', 'renter', 'car_profile']
const acceptedStatusesForChange = [
  TripStatus.PENDING,
  TripStatus.ONGOING,
  TripStatus.ACCEPTED,
]

const tripsStore = useTripsStore()

const isLoading = computed(
  () => tripsStore.tripsDetails[props.trip.id]?.isLoading
)
const isSuccess = computed(
  () => tripsStore.tripsDetails[props.trip.id]?.success || false
)

const handleUpdateStatus = (status: PrimitiveNonNull | PrimitiveNonNull[]) => {
  tripsStore.updateTripData(props.trip.id, {
    status: status as TripStatus,
  })
}

const validateWaybillData = (): string[] => {
  const errors: string[] = []
  const {
    start_date,
    renter: {
      first_name,
      last_name,
      driver_license_issue_date,
      driver_license_serial_number,
      driver_license_valid_date,
    },
    car_profile: { model_make_id, model_name, plate_number },
    end_date,
  } = props.trip

  if (!start_date) errors.push('Start date is missing')
  if (!first_name) errors.push('Renter first name is missing')
  if (!last_name) errors.push('Renter last name is missing')
  if (!model_make_id) errors.push('Car model make ID is missing')
  if (!model_name) errors.push('Car model name is missing')
  if (!driver_license_issue_date)
    errors.push('Driver license issue date is missing')
  if (!driver_license_serial_number)
    errors.push('Driver license serial number is missing')
  if (!driver_license_valid_date)
    errors.push('Driver license valid date is missing')
  if (!end_date) errors.push('End date is missing')
  if (!plate_number) errors.push('Car plate number is missing')

  return errors
}

const showValidationErrors = (): void => {
  const errors = validateWaybillData()
  if (errors.length > 0) {
    ElMessage({
      message: `Please fill in the following fields: \n${errors.join(', ')}`,
      type: 'error',
      showClose: true,
      duration: 4000,
    })
  }
}

const resendWaybill = async () => {
  const errors = validateWaybillData()
  if (errors.length > 0) {
    showValidationErrors()
    return
  }

  try {
    const response = await axios.post(
      `${process.env.VUE_APP_API_HOST}/admin/trips/waybill-resend`,
      {
        trip: props.trip,
        renter: props.trip.renter,
        host: props.trip.host,
      }
    )
  } catch (error) {
    if (error instanceof Error) {
      const axiosError = error as AxiosError
      console.error('Error status:', axiosError.response?.status)
      console.error('Error message:', axiosError.message)
    }
    console.error('Full error:', error)
  }
}

// const resendWaybill = async () => {
//   try {
//     const response = await axios.post(
//       `${process.env.VUE_APP_API_HOST}/admin/trips/waybill-resend`,
//       {
//         trip: props.trip,
//         renter: props.trip.renter,
//         host: props.trip.host,
//       }
//     )

//     response.data)
//   } catch (error) {
//     if (error instanceof Error) {
//       const axiosError = error as AxiosError
//       console.error('Error status:', axiosError.response?.status)
//       console.error('Error message:', axiosError.message)
//     }
//     console.error('Full error:', error)
//   }
// }

const statusesOptions = computed(() => {
  return [
    {
      label:
        props.trip.status === TripStatus.PENDING
          ? 'Declined by Host'
          : 'Cancelled by Host',
      value:
        props.trip.status === TripStatus.PENDING
          ? TripStatus.DECLINED_BY_HOST
          : TripStatus.CANCELLED_BY_HOST,
    },
    {
      label:
        props.trip.status === TripStatus.PENDING
          ? 'Declined by Renter'
          : 'Cancelled by Renter',
      value:
        props.trip.status === TripStatus.PENDING
          ? TripStatus.DECLINED_BY_RENTER
          : TripStatus.CANCELLED_BY_RENTER,
    },
    {
      label: 'Finished',
      value: TripStatus.FINISHED,
    },
  ]
})

const localFormatKeysForTable = (
  trip: TripFull | null,
  keys: (keyof TripFull)[]
) => {
  if (!trip) {
    return []
  }
  return keys.map(key => {
    let value = trip[key]
    if (
      key === 'admin_profit' &&
      typeof value === 'string' &&
      !isNaN(parseFloat(value))
    ) {
      value = parseFloat(value) / 100.0
    }
    return {
      key,
      value,
      label: commonCase(key, true),
    }
  })
}

const tableData = computed(() => {
  return localFormatKeysForTable(props.trip, [
    'id',
    'status',
    'commercial_host',
    'host',
    'car_profile',
    'renter',
    'waybill_sent',
    'start_date',
    'end_date',
    'pickup_address_name',
    'dropoff_address_name',
    'cancellation_policy_type',
    'daily_distance_limit',
    'extra_distance_charge',
    'admin_profit',
  ])
})

const goToEntityDetails = (
  key: 'host' | 'renter' | 'car_profile',
  id: string
) => {
  if (userKeys.includes(key)) router.push(`/users/${id}`)
  if (key === 'car_profile') router.push(`/vehicles/${id}`)
}

const copyToClipboard = (value: string) => {
  navigator.clipboard
    .writeText(value)
    .then(() => {
      ElMessage.success('Trip ID copied to clipboard')
    })
    .catch(() => {
      ElMessage.error('Failed to copy ID')
    })
}

return (_ctx: any,_cache: any) => {
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_checkbox = _resolveComponent("el-checkbox")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_table = _resolveComponent("el-table")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _cache[3] || (_cache[3] = _createElementVNode("h4", { class: "title" }, "Details section", -1)),
    _createVNode(_component_el_row, { class: "table-container" }, {
      default: _withCtx(() => [
        _createVNode(_component_el_col, { span: 24 }, {
          default: _withCtx(() => [
            _createVNode(_component_el_table, {
              data: tableData.value,
              border: false,
              "show-header": false,
              style: { width: '100%' }
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_table_column, null, {
                  default: _withCtx(({ row: { key } }) => [
                    _createTextVNode(_toDisplayString(_unref(commonCase)(key, true)) + " " + _toDisplayString(_unref(getTripKeyMeta)(key).postfix), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_table_column, { prop: "value" }, {
                  default: _withCtx(({ row: { key, value } }) => [
                    (navKeys.includes(key))
                      ? (_openBlock(), _createBlock(_component_el_row, {
                          key: 0,
                          class: "box"
                        }, {
                          default: _withCtx(() => [
                            (key === 'car_profile')
                              ? (_openBlock(), _createBlock(_component_el_col, {
                                  key: 0,
                                  span: 12
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_unref(normalizeCarName)(value)), 1)
                                  ]),
                                  _: 2
                                }, 1024))
                              : _createCommentVNode("", true),
                            (userKeys.includes(key))
                              ? (_openBlock(), _createBlock(_component_el_col, {
                                  key: 1,
                                  span: 12
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_unref(normalizeUserName)(value)), 1)
                                  ]),
                                  _: 2
                                }, 1024))
                              : _createCommentVNode("", true),
                            _createVNode(_component_el_col, {
                              span: 12,
                              push: 6,
                              class: "button-group"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_button, {
                                  plain: "",
                                  type: "primary",
                                  size: "small",
                                  icon: "el-icon-view",
                                  class: "test-btn",
                                  onClick: ($event: any) => (goToEntityDetails(key, value.id))
                                }, {
                                  default: _withCtx(() => _cache[1] || (_cache[1] = [
                                    _createTextVNode(" Show details ")
                                  ])),
                                  _: 2,
                                  __: [1]
                                }, 1032, ["onClick"])
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          _: 2
                        }, 1024))
                      : (key === 'waybill_sent')
                        ? (_openBlock(), _createBlock(_component_el_row, {
                            key: 1,
                            class: "box"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_el_col, { span: 12 }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_el_checkbox, {
                                    "model-value": value,
                                    disabled: true,
                                    class: "status-checkbox"
                                  }, null, 8, ["model-value"])
                                ]),
                                _: 2
                              }, 1024),
                              _createVNode(_component_el_col, {
                                span: 12,
                                push: 6,
                                class: "button-group-waybill"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_el_button, {
                                    plain: "",
                                    type: "primary",
                                    size: "small",
                                    icon: "el-icon-view",
                                    class: "test-btn",
                                    disabled: props.trip.waybill_sent,
                                    onClick: resendWaybill
                                  }, {
                                    default: _withCtx(() => _cache[2] || (_cache[2] = [
                                      _createTextVNode(" Resend Waybill ")
                                    ])),
                                    _: 1,
                                    __: [2]
                                  }, 8, ["disabled"])
                                ]),
                                _: 1
                              })
                            ]),
                            _: 2
                          }, 1024))
                        : (key === 'extra_distance_charge')
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                              _createTextVNode(_toDisplayString(_unref(centsToDollars)(value)), 1)
                            ], 64))
                          : (dateKeys.includes(key))
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                _createTextVNode(_toDisplayString(_unref(formatDateWithTime)(value)), 1)
                              ], 64))
                            : (key === 'status')
                              ? (_openBlock(), _createBlock(EditableSelect, {
                                  key: 4,
                                  editable: acceptedStatusesForChange.includes(value),
                                  sourceValue: value,
                                  options: statusesOptions.value,
                                  loading: isLoading.value,
                                  success: isSuccess.value,
                                  onSave: _cache[0] || (_cache[0] = ($event: any) => (handleUpdateStatus($event)))
                                }, null, 8, ["editable", "sourceValue", "options", "loading", "success"]))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                                  _createTextVNode(_toDisplayString(_unref(normalizeFalsy)(value)) + " ", 1),
                                  (key === 'id')
                                    ? (_openBlock(), _createBlock(_component_el_icon, {
                                        key: 0,
                                        class: "copy-icon",
                                        onClick: ($event: any) => (copyToClipboard(String(value))),
                                        title: "Copy Trip ID"
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_unref(CopyDocument))
                                        ]),
                                        _: 2
                                      }, 1032, ["onClick"]))
                                    : _createCommentVNode("", true)
                                ], 64))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["data"])
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ], 64))
}
}

})